import React, {useEffect, useState} from 'react'
import {Box, CircularProgress, IconButton, SvgIcon, SvgIconProps, Typography} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted'
import PeopleAltIcon from '@mui/icons-material/PeopleAlt'
// import MedicationLiquidIcon from '@mui/icons-material/MedicationLiquid' // vedi nota su "scope" più sotto
import MenuBookIcon from '@mui/icons-material/MenuBook'
import BarChartIcon from '@mui/icons-material/BarChart'
import MonitorWeightIcon from '@mui/icons-material/MonitorWeight'
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun'
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import DescriptionIcon from '@mui/icons-material/Description'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import LocalPharmacyIcon from '@mui/icons-material/LocalPharmacy'
import QuizIcon from '@mui/icons-material/Quiz'
import {useParams, useHistory} from 'react-router-dom'
import TabBar from 'src/components-new/layout/TabBar'
import StatusPill from 'src/components-new/primitives/StatusPill'
import {useAppSelector} from 'src/hooks'
import {PATHS} from 'src/constants/constants'
import apiClient from 'src/services/apiClient'
import AnagraficaTab from 'src/pages-new/patient/tabs/AnagraficaTab'
import CaregiversTab from 'src/pages-new/patient/tabs/CaregiversTab'
import DiarioTab from 'src/pages-new/patient/tabs/DiarioTab'
import AnalyticsTab from 'src/pages-new/patient/tabs/AnalyticsTab'
import TherapyTab from 'src/pages-new/patient/tabs/TherapyTab'
import DossierTab from 'src/pages-new/patient/tabs/DossierTab'
import WeightTab from 'src/pages-new/patient/tabs/WeightTab'
import PharmaciesTab from 'src/pages-new/patient/tabs/PharmaciesTab'
import SurveysTab from 'src/pages-new/patient/tabs/SurveysTab'
import GraphsTab from 'src/pages-new/patient/tabs/GraphsTab'
import ExamsTab from 'src/pages-new/patient/tabs/ExamsTab'
import ImagesTab from 'src/pages-new/patient/tabs/ImagesTab'
import PharmacovigilanceTab from 'src/pages-new/patient/tabs/PharmacovigilanceTab'
import ActivitiesTab from 'src/pages-new/patient/tabs/ActivitiesTab'
import DocumentsTab from 'src/pages-new/patient/tabs/DocumentsTab'

// MUI's built-in "Medication" icon draws a first-aid box with a cross, not a pill, so the
// therapy tab gets a dedicated capsule/pill icon here (no such glyph ships in @mui/icons-material).
function PillIcon(props: SvgIconProps): JSX.Element {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path
        d="m10.5 20.5 10-10a4.95 4.95 0 1 0-7-7l-10 10a4.95 4.95 0 1 0 7 7Z"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="m8.5 8.5 7 7"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </SvgIcon>
  )
}

function getStateColor(state: string | undefined): 'success' | 'warning' | 'error' | 'info' | 'default' {
  if (!state) return 'default'
  if (state === 'ACTIVE' || state === 'Attivo' || state === 'ACTIVE_DELIVERY') return 'success'
  if (state === 'SUSPENDED') return 'warning'
  if (state === 'EXITED') return 'error'
  return 'default'
}

// Mappatura identica a `parseStates` in src/components-new/shell/HeaderBar.tsx / src/pages/patient/Patient.tsx
function parseStateLabel(state: string | undefined): string {
  switch (state) {
    case 'TO_ACTIVE': return 'Da attivare'
    case 'ACTIVATING': return 'In attivazione'
    case 'ACTIVE': return 'Attivo'
    case 'ACTIVE_DELIVERY': return 'Attivo delivery'
    case 'SUSPENDED': return 'Sospeso'
    case 'WAITING_TO_EXIT': return 'In attesa di uscita'
    case 'EXITED': return 'Drop-out'
    default: return '-'
  }
}

interface TabDef {
  key: string
  label: string
  icon?: React.ReactElement
}

export default function PatientDetailNew(): JSX.Element {
  const {id: patientId} = useParams<{id: string}>()
  const history = useHistory()
  const [activeTab, setActiveTab] = useState<string>('patientData')
  const [patientHeader, setPatientHeader] = useState<{name: string; state: string; firstName?: string; lastName?: string; serviceConf?: string; code?: string} | null>(null)

  useEffect(() => {
    let cancelled = false
    apiClient.getPatientsUserData(patientId, () => {}).then((data: any) => {
      if (!cancelled && data) {
        setPatientHeader({
          name: [data.firstName, data.lastName].filter(Boolean).join(' ') || 'Paziente',
          state: data.state ?? '',
          firstName: data.firstName,
          lastName: data.lastName,
          serviceConf: data.serviceConf,
          code: data?.code,
        })
      }
    })
    return () => { cancelled = true }
  }, [patientId])

  const patientDetailConfiguration = useAppSelector(
    state => state.pspConfigReducer.data.patientDetailConfiguration
  )
  // Non più usato da quando "therapy" mostra sempre PillIcon, ma tenuto commentato: la
  // distinzione per scope 'tutor' (flebo/farmaco liquido vs pillola) potrebbe servire ancora.
  // const scope = useAppSelector(state => state.pspConfigReducer.data.scope)
  const tabsConfig = patientDetailConfiguration?.tabs ?? {}

  // Build tabs — every entry (including patientData/diary/analytics) is only shown if the PSP
  // config marks it visible, mirroring the legacy Patient.tsx TABS.filter(tab => tabs[tab.id]?.show).
  const TAB_DEFS: {key: string; defaultLabel: string}[] = [
    {key: 'patientData', defaultLabel: 'Anagrafica'},
    {key: 'caregivers', defaultLabel: 'Caregiver/Tutori'},
    {key: 'pharmacies', defaultLabel: 'Farmacie'},
    {key: 'therapy', defaultLabel: 'Terapie'},
    {key: 'dossier', defaultLabel: 'Dossier'},
    {key: 'diary', defaultLabel: 'Diario'},
    {key: 'analytics', defaultLabel: 'Analytics'},
    {key: 'activities', defaultLabel: 'Attività'},
    {key: 'weight', defaultLabel: 'Peso'},
    {key: 'images', defaultLabel: 'Immagini'},
    {key: 'exams', defaultLabel: 'Esami'},
    {key: 'pharmacovigilance', defaultLabel: 'Farmacovigilanza'},
    {key: 'surveys', defaultLabel: 'Questionari'},
    {key: 'graphs', defaultLabel: 'Grafici'},
    {key: 'documents', defaultLabel: 'Documenti'}
  ]

  // Icona per tab, equivalente alle @mdi/js usate nel legacy Patient.tsx (righe 58-133) ma
  // rimappate su @mui/icons-material per restare coerenti con lo stack icone della nuova UI.
  // "therapy" usa sempre l'icona a pillola (vedi PillIcon sopra), indipendentemente dal programma.
  // In precedenza lo scope 'tutor' mostrava MedicationLiquidIcon (flebo/farmaco liquido); se
  // dovesse servire di nuovo, riattivare la riga `scope` sopra e usare:
  // therapy: scope === 'tutor' ? <MedicationLiquidIcon fontSize="small" /> : <PillIcon fontSize="small" />,
  const TAB_ICONS: Record<string, React.ReactElement> = {
    patientData: <FormatListBulletedIcon fontSize="small" />,
    caregivers: <PeopleAltIcon fontSize="small" />,
    therapy: <PillIcon fontSize="small" />,
    dossier: <FormatListBulletedIcon fontSize="small" />,
    diary: <MenuBookIcon fontSize="small" />,
    analytics: <BarChartIcon fontSize="small" />,
    weight: <MonitorWeightIcon fontSize="small" />,
    activities: <DirectionsRunIcon fontSize="small" />,
    images: <PhotoLibraryIcon fontSize="small" />,
    pharmacovigilance: <WarningAmberIcon fontSize="small" />,
    exams: <DescriptionIcon fontSize="small" />,
    documents: <UploadFileIcon fontSize="small" />,
    pharmacies: <LocalPharmacyIcon fontSize="small" />,
    surveys: <QuizIcon fontSize="small" />,
    graphs: <BarChartIcon fontSize="small" />
  }

  // "secondaryTherapy" è un toggle opt-out: prima che esistesse questo campo la scheda "Terapie
  // accessorie" era sempre visibile, quindi in assenza del campo (programmi non ancora
  // riconfigurati) resta visibile di default.
  function isTabVisible(tabDef: {key: string}): boolean {
    if (tabDef.key === 'therapy') {
      return Boolean(tabsConfig.therapy?.show || tabsConfig.secondaryTherapy?.show !== false)
    }
    return Boolean(tabsConfig[tabDef.key]?.show)
  }

  const tabs: TabDef[] = TAB_DEFS
    .filter(isTabVisible)
    .map(tabDef => ({
      key: tabDef.key,
      label: tabsConfig[tabDef.key]?.label ?? tabDef.defaultLabel,
      icon: TAB_ICONS[tabDef.key]
    }))

  function renderTabContent(): JSX.Element | null {
    switch (activeTab) {
      case 'patientData':
        return <AnagraficaTab patientId={patientId} profileConfig={patientDetailConfiguration?.profileConfig} />
      case 'caregivers':
        return <CaregiversTab patientId={patientId} />
      case 'therapy':
        return <TherapyTab patientId={patientId} />
      case 'dossier':
        return <DossierTab patientId={patientId} firstName={patientHeader?.firstName} lastName={patientHeader?.lastName} />
      case 'diary':
        return <DiarioTab patientId={patientId} serviceConf={patientHeader?.serviceConf} userCode={patientHeader?.code} userFirstName={patientHeader?.firstName} userLastName={patientHeader?.lastName} />
      case 'analytics':
        return <AnalyticsTab patientId={patientId} />
      case 'weight':
        return <WeightTab patientId={patientId} firstName={patientHeader?.firstName} lastName={patientHeader?.lastName} />
      case 'activities':
        return <ActivitiesTab patientId={patientId} />
      case 'images':
        return <ImagesTab patientId={patientId} />
      case 'pharmacovigilance':
        return <PharmacovigilanceTab patientId={patientId} />
      case 'exams':
        return <ExamsTab patientId={patientId} />
      case 'documents':
        return <DocumentsTab patientId={patientId} />
      case 'pharmacies':
        return <PharmaciesTab patientId={patientId} />
      case 'surveys':
        return <SurveysTab patientId={patientId} />
      case 'graphs':
        return <GraphsTab patientId={patientId} />
      default:
        return null
    }
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 3}}>
        <IconButton
          onClick={() => history.push(PATHS.PATIENTS)}
          size="small"
          aria-label="Torna alla lista pazienti"
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h1" sx={{flex: 1}}>
          {patientHeader ? patientHeader.name : <CircularProgress size={20} />}
        </Typography>
        {patientHeader?.state && (
          <StatusPill
            label={parseStateLabel(patientHeader.state)}
            color={getStateColor(patientHeader.state)}
          />
        )}
      </Box>

      {/* Tabs */}
      <Box sx={{borderBottom: '1px solid', borderColor: 'divider', mb: 3}}>
        <TabBar
          tabs={tabs}
          activeKey={activeTab}
          onChange={setActiveTab}
        />
      </Box>

      {/* Tab content */}
      {renderTabContent()}
    </Box>
  )
}
